import React from 'react'
import clsx from 'clsx'
import { Row, Cell, flexRender } from '@tanstack/react-table'
import { ArrowRight2 } from 'iconsax-react'

import { useBreakpoint } from '@SonarCX/providers/BreakpointProvider'

export type CellComponentProps<T> = {
  cellKey: string
  index: number
  row: Row<T>
  cell: Cell<T, unknown>
  isExpanded: boolean
  renderRowSubComponent: (row: Row<T>) => React.ReactNode
  canExpandRow: (row: T) => boolean
  onToggleExpanded: (id: string, expanded: boolean) => void
  onRowClicked: (row: Row<T>) => void
}

const CellComponent = <T extends unknown>({
  cellKey,
  index,
  row,
  cell,
  isExpanded,
  renderRowSubComponent,
  canExpandRow,
  onToggleExpanded,
  onRowClicked
}: CellComponentProps<T>) => {
  const { isSmall } = useBreakpoint()

  const hasSub = React.useMemo(
    () => renderRowSubComponent && onToggleExpanded,
    [renderRowSubComponent, onToggleExpanded]
  )
  const isFirstCell = React.useMemo(() => index === 0, [index])
  const rendered = React.useMemo(
    () => flexRender(cell.column.columnDef.cell, cell.getContext()),
    [cell]
  )

  const handleCategoryClick = React.useCallback(
    e => {
      e.stopPropagation()
      onToggleExpanded(row.id, !Boolean(isExpanded))
    },
    [isExpanded, row.id, onToggleExpanded]
  )

  const handleExpand = React.useCallback(
    e => {
      e.stopPropagation()
      // @ts-ignore
      const id = row.original.id
      const open = !Boolean(isExpanded)
      onToggleExpanded(id, open)
    },
    [row.original, isExpanded, onToggleExpanded]
  )

  if (cell.getIsGrouped()) {
    return (
      <div key={cellKey} onClick={handleCategoryClick} className='table-data'>
        <ArrowRight2
          variant='Bold'
          onClick={handleCategoryClick}
          className={clsx(
            'arrow',
            isExpanded && 'arrow-down',
            isSmall && 'center-arrow'
          )}
        />

        <div className='section'>
          <div className='title'>{rendered}</div>

          <div className='count'>{row.subRows.length}</div>
        </div>
      </div>
    )
  }

  if (cell.getIsAggregated()) {
    return null
  }

  return (
    <div
      key={cellKey}
      onClick={e => {
        e.stopPropagation()

        // @ts-ignore
        if (e.target.className === 'table-data') {
          onRowClicked && onRowClicked(row)
        }
      }}
      className='table-data'
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        minHeight: '36px'
      }}
    >
      {hasSub &&
        isFirstCell &&
        (canExpandRow(row.original) ? (
          <ArrowRight2
            variant='Bold'
            onClick={handleExpand}
            className={clsx(
              'arrow',
              isExpanded && 'arrow-down',
              isSmall && 'center-arrow'
            )}
          />
        ) : (
          <div
            className='arrow'
            style={{
              cursor: 'default',
              width: '1rem'
            }}
          />
        ))}

      {rendered}
    </div>
  )
}

export default CellComponent
